/**
 * chat-dialog.js — AI 客服對話框
 *
 * 怎麼被載入：main.js（前台）與 admin.js（後台）皆 import；底部 querySelector
 *   .ai-fab + #chat-dialog 守門早退，無對應 DOM 即 no-op。觸發鈕（.ai-fab）永遠固定可見
 *   （CSS-only，無 JS 顯隱閘門 — v17 起舊 ai-fab.js 已移除），本檔只負責「點 fab 開對話框」之後的對話互動。
 *
 * 觸發：點 .ai-fab 開啟，點 .chat-dialog__close / 點 backdrop / Esc 關閉。
 * 內容：歡迎訊息 + Quick Replies → 點擊衍生分支 → 開放輸入走 fallback / 醫療關鍵字偵測。
 * 無障礙：開啟後 focus input、Tab focus-trap 鎖在 dialog 內、關閉還焦點給 fab。
 * 對話歷史不持久化（關閉後清空 body，重開回到初始）。
 *
 * DEMO: 本檔是純前端 mock，無 LLM / 無任何 API 呼叫。所有「AI 回覆」皆為下方寫死的
 *   罐頭腳本（RESPONSES / FALLBACK / MEDICAL_REJECT），showTyping() 的打字延遲是
 *   setTimeout 演出、非真實串流。接後端時的整合點見下方各 TODO(backend:) 標記。
 *
 * 已知陷阱：
 *   - fab 的 click listener 在此檔綁（openDialog）；ai-fab.js 不可再對 fab 綁 alert
 *     之類的舊行為（addEventListener 匿名函式無法移除，會疊加）。
 *   - getFocusable() 每次即時查（quick-reply 按鈕動態增減），不可快取。
 */

// focus-trap selector + getFocusable + Tab 循環 → 收斂至 utils/focus-trap.js（單一真相源）。
// 行為變更（皆刻意統一、a11y 改善）：
//   1. selector 從本檔原 4-token 升為 canonical 6-token —— 補回 select / textarea
//      （原本漏：dialog 內的 <select>/<textarea> 會跳出 trap = 真鍵盤 a11y 缺陷，現已納入循環）
//      + input 的 :not([type="hidden"]) 守衛。
//   2. getFocusable filter 改 keep-active-el-if-hidden（原僅 offsetParent!==null）。
//   本檔原 Tab handler 已含「焦點逃出 dialog 即拉回」逃逸復原守衛 → 即 trapTab 的 superset 行為，等價。
// trapTab 內部即時查 getFocusable(dialog)（quick-reply 按鈕動態增減，每次重查、不快取），故本檔只 import trapTab。
import { trapTab } from './utils/focus-trap.js';
// P-H-3：改 import 共用 prefersReducedMotion()（utils/dom.js），去重原 inline matchMedia 判斷。
import { prefersReducedMotion } from './utils/dom.js';

// ===== 對話資料（perry 拍板腳本） =====
// DEMO: 以下全為寫死的罐頭對話腳本（非後端回傳）。
// TODO(backend): 接 LLM 後，WELCOME / RESPONSES / FALLBACK / MEDICAL_REJECT 改由後端
//   回傳；前端只保留 quick-reply 的 UI 殼。醫療關鍵字攔截（MEDICAL_KEYWORDS）建議保留
//   為前端第一道防線，避免把醫療診斷類問題送進模型。

const WELCOME_MSG = `你好，我是默證導覽。
我可以幫你了解平台評鑑機制、找到認證診所，或引導你比較療程資訊。
請問你目前在找什麼？`;

const WELCOME_QUICK_REPLIES = [
  { label: '如何挑選診所', key: 'select-clinic' },
  { label: '療程怎麼比較', key: 'compare-treatment' },
  { label: '評鑑標準是什麼', key: 'standards' },
  { label: '看最新專欄', key: 'latest-articles' },
];

const RESPONSES = {
  'select-clinic': {
    msg: `挑診所，默證建議你確認三件事：

1. 認證狀態：診所頁面左上角有 MedProof 標章，才是通過平台審核的診所。
2. 醫師資歷：每間認證診所都有醫師介紹頁，可確認專業領域與執業年資。
3. 真實評價：專欄中的療程分享由使用者撰寫、醫師核查，不是業配。

你可以直接到診所頁面用地區或療程類別篩選。`,
    quickReplies: [
      { label: '查看認證診所', key: 'view-clinics' },
      { label: '了解認證標準', key: 'standards' },
      { label: '看療程分享', key: 'latest-articles' },
    ],
  },
  'compare-treatment': {
    msg: `比較療程，先確認你的需求落在哪個類別：拉提緊緻、雷射光電、注射療程、體雕塑形各有不同適應症與恢復期。

默證專欄有分類整理的療程比較文章，每篇都經醫師審核、標明資料來源，不是廣告軟文。

建議你從探索主題頁選擇你在意的療程類別，或在搜尋列輸入療程名稱，直接找到對應的分析文章。`,
    quickReplies: [
      { label: '看療程比較文章', key: 'latest-articles' },
      { label: '用類別探索', key: 'explore' },
      { label: '看認證診所', key: 'view-clinics' },
    ],
  },
  'standards': {
    msg: `默證的評鑑機制分三層：

VERIFIED：診所資料與醫師執照經平台人工比對，確認資訊正確有出處。
CERTIFIED：診所通過審核流程，符合平台訂定的掛牌標準，才能出現在結果頁。
WITNESSED：使用者的療程分享送審後，由醫師確認內容不違背醫學原則才發布。

任何診所或文章，只要出現在默證平台，都已經過對應層次的把關。`,
    quickReplies: [
      { label: '查看認證診所', key: 'view-clinics' },
      { label: '看審核專欄', key: 'latest-articles' },
      { label: '關於我們', key: 'about' },
    ],
  },
  'latest-articles': {
    msg: `最新專欄收錄療程比較、價格指南、術後保養、醫師觀點四個方向，每篇文章都標示審核醫師與發布日期。

目前最新三篇：
- 皮秒雷射 vs 飛梭雷射：術前你該先搞清楚這件事
- 2026 年台灣電波拉提收費行情全解析
- 玻尿酸施打後 72 小時，這些動作會讓效果打折

可以直接到專欄頁瀏覽全部文章，或用標籤篩選你在意的主題。`,
    quickReplies: [
      { label: '瀏覽所有專欄', key: 'all-articles' },
      { label: '看療程比較', key: 'compare-treatment' },
      { label: '看醫師觀點', key: 'doctor-views' },
    ],
  },
  // view-clinics / explore / about / all-articles / doctor-views 無 entry → 走 fallback
};

const FALLBACK_OPEN_INPUT = {
  msg: `你的問題我收到了。目前默證導覽以平台機制說明與資訊引導為主，無法即時回覆開放式問題。

完整的 AI 對話功能正在開發中。

現在你可以回到選單繼續導覽，或直接前往以下頁面：`,
  quickReplies: [
    { label: '回到主選單', key: 'main-menu' },
    { label: '瀏覽診所', key: 'view-clinics' },
    { label: '瀏覽專欄', key: 'all-articles' },
  ],
};

const MEDICAL_QUERY_REJECT = {
  msg: `這類問題需要醫師面診評估，我無法提供個人化的醫療判斷或建議。

如果你有術後不適，建議直接聯繫原施術診所；若需要諮詢，可以在默證找到通過認證的診所。`,
  quickReplies: [
    { label: '找認證診所', key: 'view-clinics' },
    { label: '回到主選單', key: 'main-menu' },
  ],
};

// 醫療診斷類關鍵字（粗略偵測，false positive 接受，v2 改 LLM 不需要）。
// DEMO: substring 比對的土砲偵測，僅供 demo 演出「會擋醫療提問」的行為。
const MEDICAL_KEYWORDS = [
  '副作用', '不舒服', '腫', '紅', '癢', '痛',
  '適合', '我能不能', '我可以', '出血', '紅斑',
];

const TYPING_DELAY_MS = 800;

// ===== DOM refs =====

const fab = document.querySelector('.ai-fab');
const dialog = document.getElementById('chat-dialog');

// 若 DOM 不在（其他頁面），直接 noop
if (fab && dialog) {
  const backdrop = document.getElementById('chat-dialog-backdrop');
  const body = document.getElementById('chat-dialog-body');
  const form = document.getElementById('chat-dialog-form');
  const input = document.getElementById('chat-dialog-input');
  const closeBtn = dialog.querySelector('.chat-dialog__close');

  // focus trap：Tab 循環全交 trapTab(e, dialog)（檔頭 import）；其內部即時查 dialog 內可聚焦元素
  // （quick-reply 按鈕動態增減，每次重查、不快取）。selector / getFocusable / Tab 循環收斂於 utils/focus-trap.js。

  const prm = prefersReducedMotion();
  const focusDelay = prm ? 0 : 240; // 對話框動畫結束後再 focus

  let isInitialized = false;

  // ===== Render helpers =====

  function addMessage(content, type = 'ai') {
    const msg = document.createElement('div');
    msg.className = `chat-msg chat-msg--${type}`;
    msg.textContent = content;
    body.appendChild(msg);
    scrollToBottom();
    return msg;
  }

  function addQuickReplies(replies) {
    const wrapper = document.createElement('div');
    wrapper.className = 'chat-quick-replies';

    const frag = document.createDocumentFragment();
    replies.forEach(({ label, key }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chat-quick-reply';
      btn.textContent = label;
      btn.dataset.key = key;
      btn.addEventListener('click', () => handleQuickReply(label, key));
      frag.appendChild(btn);
    });
    wrapper.appendChild(frag);

    body.appendChild(wrapper);
    scrollToBottom();
    return wrapper;
  }

  function showTyping() {
    const typing = document.createElement('div');
    typing.className = 'chat-typing';
    // 3 個 dot；用 createElement 避免 innerHTML
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('span');
      dot.className = 'chat-typing__dot';
      typing.appendChild(dot);
    }
    body.appendChild(typing);
    scrollToBottom();
    return typing;
  }

  function scrollToBottom() {
    body.scrollTop = body.scrollHeight;
  }

  // ===== 對話流程 handlers =====

  /**
   * 點 quick-reply：先回顯使用者選的 label，演出打字後給對應罐頭回覆。
   * 'main-menu' 回到歡迎選單；命中 RESPONSES 給該分支；其餘 key 走 fallback。
   * DEMO: typing 延遲是演出，回覆查本地 RESPONSES table，非後端。
   * TODO(backend): 改成把 key（或 label）送後端、await 回覆後 render；保留 showTyping 當載入態。
   */
  async function handleQuickReply(label, key) {
    addMessage(label, 'user');

    const typing = showTyping();
    await new Promise((r) => setTimeout(r, TYPING_DELAY_MS));
    typing.remove();

    if (key === 'main-menu') {
      addMessage(WELCOME_MSG, 'ai');
      addQuickReplies(WELCOME_QUICK_REPLIES);
      return;
    }

    const response = RESPONSES[key];
    if (response) {
      addMessage(response.msg, 'ai');
      addQuickReplies(response.quickReplies);
    } else {
      // 未定義的 key（view-clinics / about / all-articles / explore / doctor-views）走 fallback
      addMessage(FALLBACK_OPEN_INPUT.msg, 'ai');
      addQuickReplies(FALLBACK_OPEN_INPUT.quickReplies);
    }
  }

  /**
   * 開放輸入框送出：醫療關鍵字命中 → 婉拒並導去找診所；否則一律 fallback（無真實理解）。
   * DEMO: 沒有任何語意理解，只做 MEDICAL_KEYWORDS substring 比對 + 固定回覆兩條路。
   * TODO(backend): 改成把 text 送 LLM、await 回覆後 render；醫療攔截建議保留為送出前的前端守門。
   * TODO(backend): handleUserInput/handleQuickReply 改送後端 LLM、await 回覆後 render（保留 MEDICAL_KEYWORDS 守門） — 見 docs/BACKEND_HANDOFF.md#ai-chat-dialog
   */
  async function handleUserInput(text) {
    addMessage(text, 'user');

    const typing = showTyping();
    await new Promise((r) => setTimeout(r, TYPING_DELAY_MS));
    typing.remove();

    const isMedical = MEDICAL_KEYWORDS.some((kw) => text.includes(kw));
    if (isMedical) {
      addMessage(MEDICAL_QUERY_REJECT.msg, 'ai');
      addQuickReplies(MEDICAL_QUERY_REJECT.quickReplies);
    } else {
      addMessage(FALLBACK_OPEN_INPUT.msg, 'ai');
      addQuickReplies(FALLBACK_OPEN_INPUT.quickReplies);
    }
  }

  // ===== 開關邏輯 =====

  function openDialog() {
    if (!isInitialized) {
      addMessage(WELCOME_MSG, 'ai');
      addQuickReplies(WELCOME_QUICK_REPLIES);
      isInitialized = true;
    }
    dialog.classList.add('is-open');
    dialog.setAttribute('aria-hidden', 'false');
    if (backdrop) {
      backdrop.classList.add('is-open');
      backdrop.setAttribute('aria-hidden', 'false');
    }
    setTimeout(() => input.focus(), focusDelay);
  }

  function closeDialog() {
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    if (backdrop) {
      backdrop.classList.remove('is-open');
      backdrop.setAttribute('aria-hidden', 'true');
    }
    fab.focus();
    // 規格：對話歷史不存，重開回到初始
    // 動畫結束後清 body（240ms transition + buffer）；prefers-reduced-motion 0ms
    const clearDelay = prm ? 0 : 280;
    setTimeout(() => {
      body.innerHTML = '';
      isInitialized = false;
    }, clearDelay);
  }

  // ===== 綁定 =====

  // 取代 ai-fab.js 既有 alert：移除 fab 上的舊 listener 不可行（addEventListener 無法移除匿名函式）
  // 改在 ai-fab.js 直接刪 alert，這裡僅綁開啟邏輯
  fab.addEventListener('click', openDialog);

  closeBtn.addEventListener('click', closeDialog);

  // 點半透明遮罩關閉（等同點對話框外部）
  if (backdrop) {
    backdrop.addEventListener('click', closeDialog);
  }

  document.addEventListener('keydown', (e) => {
    if (!dialog.classList.contains('is-open')) return;

    // Esc 關閉
    if (e.key === 'Escape') {
      closeDialog();
      return;
    }

    // Tab focus trap：鎖在 dialog 內循環，不漏到背景頁面（含焦點逃出時拉回）。
    if (e.key === 'Tab') {
      trapTab(e, dialog);
    }
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    handleUserInput(text);
  });
}
